import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { SessionRenewalMonitorService } from '../../../core/services/session-renewal-monitor.service';
import { ButtonComponent } from '../design-system/button.component';

@Component({
  selector: 'app-session-renewal-alert',
  standalone: true,
  imports: [ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (monitor.visible()) {
      <aside
        class="session-renewal-alert"
        role="alertdialog"
        aria-labelledby="session-renewal-title"
        aria-describedby="session-renewal-desc"
      >
        <button
          type="button"
          class="session-renewal-alert__close"
          aria-label="Fechar aviso"
          (click)="monitor.dismiss()"
        >
          <i class="bi bi-x-lg" aria-hidden="true"></i>
        </button>

        <div class="session-renewal-alert__icon" aria-hidden="true">
          <i class="bi bi-hourglass-split"></i>
        </div>

        <h2 id="session-renewal-title" class="session-renewal-alert__title">
          Sua sessão está expirando
        </h2>
        <p id="session-renewal-desc" class="session-renewal-alert__message">
          A sessão expira em <strong>{{ monitor.timeLabel() }}</strong>.
          Renove agora para continuar sem interrupções.
        </p>

        <div class="session-renewal-alert__actions">
          <app-button
            label="Renovar sessão"
            icon="bi-arrow-clockwise"
            class="primary sm"
            [loading]="monitor.renewing()"
            loadingLabel="Renovando..."
            (click)="monitor.renew()"
          />
        </div>
      </aside>
    }
  `,
  styles: `
    @use 'variables' as *;
    @use 'sass:map';

    .session-renewal-alert {
      position: fixed;
      right: $space-4;
      bottom: $space-4;
      z-index: 1080;
      width: min(22rem, calc(100vw - #{$space-4} * 2));
      padding: $space-5;
      border-radius: $radius-xl;
      border: 1px solid $color-warning-light;
      background: $color-bg-white;
      box-shadow: $shadow-lg;
      animation: session-renewal-in 0.28s ease-out;
    }

    .session-renewal-alert__close {
      position: absolute;
      top: $space-3;
      right: $space-3;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: none;
      border-radius: $radius-md;
      background: transparent;
      color: $color-text-light;
      cursor: pointer;
    }

    .session-renewal-alert__close:hover {
      background: $color-bg-light;
      color: $color-text-dark;
    }

    .session-renewal-alert__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      margin-bottom: $space-3;
      border-radius: $radius-full;
      background: $color-warning-light;
      color: $color-warning-dark;
      font-size: 1.25rem;
    }

    .session-renewal-alert__title {
      margin: 0 0 $space-2;
      padding-right: $space-6;
      font-size: $font-size-md;
      font-weight: 700;
      color: $color-text-dark;
      line-height: 1.3;
    }

    .session-renewal-alert__message {
      margin: 0 0 $space-4;
      font-size: $font-size-sm;
      color: $color-text-light;
      line-height: 1.5;
    }

    .session-renewal-alert__actions {
      display: flex;
      justify-content: flex-end;
    }

    @keyframes session-renewal-in {
      from {
        opacity: 0;
        transform: translateY(12px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: map.get($breakpoints, 'sm')) {
      .session-renewal-alert {
        right: $space-3;
        bottom: $space-3;
        width: calc(100vw - #{$space-3} * 2);
      }
    }
  `,
})
export class SessionRenewalAlertComponent {
  protected readonly monitor = inject(SessionRenewalMonitorService);
}
